/**
 * Estado e submit do sheet de criar/editar follow-up.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { notify } from '../../../../utils/toast';
import { DEFAULT_FOLLOW_UP_DELAY } from '../followUp.constants';
import { useFollowUpSettings } from './useFollowUpSettings';
import type { FollowUpTemplateDraft } from './useFollowUpTemplates';
import type { FollowUpDelayUnit, FollowUpSheetPayload } from '../types';

export type FollowUpSheetTab = 'mensagem' | 'destino';

export function useFollowUpFormSheet(options: {
    isOpen: boolean;
    template?: FollowUpTemplateDraft | null;
    onSave: (payload: FollowUpSheetPayload, options?: { silent?: boolean }) => void | Promise<void>;
    onClose: () => void;
}) {
    const { isOpen, template, onSave, onClose } = options;
    const isEditing = Boolean(template);
    const [tab, setTab] = useState<FollowUpSheetTab>('mensagem');
    const [message, setMessage] = useState('');
    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [delayValue, setDelayValue] = useState(DEFAULT_FOLLOW_UP_DELAY.delayValue);
    const [delayUnit, setDelayUnit] = useState<FollowUpDelayUnit>(DEFAULT_FOLLOW_UP_DELAY.delayUnit);
    const [isSaving, setIsSaving] = useState(false);
    const settings = useFollowUpSettings();
    const fieldId = template?.id ?? 'followup-new';
    const isMessageStep = tab === 'mensagem';
    const lastSaved = useRef('');
    const pendingRef = useRef<FollowUpSheetPayload | null>(null);
    const wasOpen = useRef(false);
    const reloadedForOpen = useRef(false);
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;
    const reloadSettings = settings.reload;

    const templateId = template?.id;
    const templateMessage = template?.message;
    const templateMediaUrl = template?.mediaUrl ?? null;
    const templateDelayValue = template?.delayValue;
    const templateDelayUnit = template?.delayUnit;

    useEffect(() => {
        if (!isOpen) {
            lastSaved.current = '';
            reloadedForOpen.current = false;
            return;
        }
        setTab('mensagem');
        setMessage(templateMessage ?? '');
        setMediaUrl(templateMediaUrl);
        setDelayValue(templateDelayValue ?? DEFAULT_FOLLOW_UP_DELAY.delayValue);
        setDelayUnit(templateDelayUnit ?? DEFAULT_FOLLOW_UP_DELAY.delayUnit);
        lastSaved.current = templateId
            ? JSON.stringify({
                message: (templateMessage ?? '').trim(),
                mediaUrl: templateMediaUrl,
                delayValue: templateDelayValue,
                delayUnit: templateDelayUnit,
            })
            : '';
    }, [isOpen, templateId, templateMessage, templateMediaUrl, templateDelayValue, templateDelayUnit]);

    useEffect(() => {
        if (!isOpen) return;
        if (reloadedForOpen.current) return;
        reloadedForOpen.current = true;
        reloadSettings();
    }, [isOpen, reloadSettings]);

    useEffect(() => {
        if (!isOpen || !templateId || !message.trim()) {
            pendingRef.current = null;
            return;
        }
        const payload: FollowUpSheetPayload = {
            message: message.trim(),
            mediaUrl,
            delayValue,
            delayUnit,
        };
        const snapshot = JSON.stringify(payload);
        if (snapshot === lastSaved.current) {
            pendingRef.current = null;
            return;
        }
        pendingRef.current = payload;
        const timer = window.setTimeout(() => {
            lastSaved.current = snapshot;
            pendingRef.current = null;
            void onSaveRef.current(payload, { silent: true });
        }, 700);
        return () => window.clearTimeout(timer);
    }, [isOpen, templateId, message, mediaUrl, delayValue, delayUnit]);

    useEffect(() => {
        if (isOpen) {
            wasOpen.current = true;
            return;
        }
        if (!wasOpen.current) return;
        wasOpen.current = false;
        const pending = pendingRef.current;
        if (!pending) return;
        pendingRef.current = null;
        void onSaveRef.current(pending, { silent: true });
    }, [isOpen]);

    const goToDestination = () => {
        if (!message.trim()) {
            notify.warning('Preencha a mensagem do follow-up.');
            return;
        }
        setTab('destino');
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (isMessageStep) {
            goToDestination();
            return;
        }
        if (!message.trim()) {
            notify.warning('Preencha a mensagem do follow-up.');
            setTab('mensagem');
            return;
        }
        if (settings.isLoaded && !settings.loadError && !settings.canSaveDestination) {
            notify.warning('Confira o horário comercial em Destino.');
            setTab('destino');
            return;
        }

        setIsSaving(true);
        try {
            if (settings.isLoaded && !settings.loadError && settings.isDestinationDirty) {
                await settings.saveDestination();
            }
            await onSave({
                message: message.trim(),
                mediaUrl,
                delayValue,
                delayUnit,
            });
            onClose();
        } catch {
            // erro já notificado nos hooks
        } finally {
            setIsSaving(false);
        }
    };

    return {
        isEditing,
        tab,
        setTab,
        message,
        setMessage,
        mediaUrl,
        setMediaUrl,
        delayValue,
        setDelayValue,
        delayUnit,
        setDelayUnit,
        isSaving,
        settings,
        fieldId,
        isMessageStep,
        handleSubmit,
    };
}
